import React, { useState, useEffect, useCallback } from 'react';
import {
  Badge,
  Button,
  Card,
  Modal,
  Spinner,
  Alert,
  Form,
  Row,
  Col,
  InputGroup,
  Table,
  Container,
} from 'react-bootstrap';
import axios from 'axios'; // Use axios directly as 'api' is undefined without context
import './AdminLayout.css';
import {
  MapContainer,
  TileLayer,
  Marker,
  useMapEvents,
  Popup,
  useMap,
} from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import OrderTracking from './OrderTracking';
import L from 'leaflet';
import { GeoSearchControl, OpenStreetMapProvider } from 'leaflet-geosearch';
import 'leaflet-geosearch/dist/geosearch.css';
import BASE_URL, { BACKEND_URL } from '../config';
import AdminOrderDetailsModal from './AdminOrderDetailsModal';
import {
  FaBoxOpen,
  FaMapMarkerAlt,
  FaTruck,
  FaShoppingCart,
  FaClock,
  FaSpinner,
  FaCheckCircle,
  FaDollarSign,
  FaUser,
  FaChevronDown,
} from 'react-icons/fa'; // Added icons for dashboard cards

// --- AXIOS INSTANCE ---
const authAxios = axios.create({ baseURL: BASE_URL });
authAxios.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});
const api = authAxios; // Using authAxios as the main API instance

// Fix for Leaflet marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
});

// Custom icons for different markers
const createIcon = (color, icon = null) => {
  return L.divIcon({
    className: 'custom-div-icon',
    html: `<div style="background-color: ${color}; width: 30px; height: 30px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.4); display: flex; align-items: center; justify-content: center; color: white; font-size: 14px; font-weight: bold;">${
      icon || ''
    }</div>`,
    iconSize: [30, 30],
    iconAnchor: [15, 15],
  });
};
const pickupIcon = createIcon('#28a745', 'Pin');
const dropIcon = createIcon('#dc3545', 'Flag');
const driverIcon = createIcon('#007bff', 'Car');

// Component to add search control to the map
const SearchBar = ({ onSearchSelect }) => {
  const map = useMap();
  useEffect(() => {
    const provider = new OpenStreetMapProvider({
      params: {
        'accept-language': 'en',
      },
    });
    const searchControl = new GeoSearchControl({
      provider,
      showMarker: false,
      showPopup: false,
      style: 'bar',
      autoClose: true,
      keepResult: false,
      searchLabel: 'Search for address...',
    });
    map.addControl(searchControl);
    const handleSearchSelect = (event) => {
      const { location } = event;
      onSearchSelect(location);
    };
    map.on('geosearch/showlocation', handleSearchSelect);
    return () => {
      map.removeControl(searchControl);
      map.off('geosearch/showlocation', handleSearchSelect);
    };
  }, [map, onSearchSelect]);
  return null;
};

// 🌟 Attachment Upload function
const uploadAttachment = async (orderId, file, userId, orderStatus) => {
  if (!file) return null;
  const attachableId = Number(orderId);
  if (isNaN(attachableId)) return null;

  const formData = new FormData();
  const typeMap = {
    pending: 'order_pending',
    assigned: 'order_pending',
    accepted: 'order_pending',
    picked_up: 'order_picked_up',
    in_transit: 'order_in_transit',
    delivered: 'order_delivered',
    returned: 'order_returned',
    rejected: 'order_rejected',
    completed: 'order_delivered',
  };

  formData.append(
    'attachable_type',
    typeMap[orderStatus?.toLowerCase()] || 'order_pending'
  );
  formData.append('attachable_id', attachableId);
  // Assuming you can get the user ID here or pass it from state
  const currentUserId = localStorage.getItem('userId') || userId;
  formData.append('uploaded_by_id', currentUserId);

  let type = 'order_document';
  if (file.type.startsWith('image/')) type = 'pod_photo';
  else if (file.type.startsWith('audio/')) type = 'voice_note';
  formData.append('type', type);
  formData.append('file', file);

  try {
    const res = await api.post('/attachments', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data;
  } catch (err) {
    console.error('Attachment upload failed:', err.response?.data || err);
    return null;
  }
};

// 🌟 Function to map raw API attachments to structured format 🌟
const mapOrderAttachments = (order) => {
  // Check for nested or direct array structure
  const rawAttachments = Array.isArray(order.attachments?.data)
    ? order.attachments.data
    : Array.isArray(order.attachments)
    ? order.attachments
    : [];

  const pod_photos = rawAttachments.filter(
    (a) => a.type?.toLowerCase() === 'pod_photo'
  );
  const voice_notes = rawAttachments.filter(
    (a) => a.type?.toLowerCase() === 'voice_note'
  );
  const order_docs = rawAttachments.filter(
    (a) => a.type?.toLowerCase() === 'order_document'
  );

  return {
    ...order,
    // Ensure nested objects are present before accessing properties
    customer: order.customer || {},
    vendor: order.vendor || {},
    driver: order.driver || {},
    showroom: order.showroom || {},

    customer_name:
      order.customer?.name || order.customer_id
        ? `Customer #${order.customer_id}`
        : 'N/A',
    vendor_name:
      order.vendor?.name || order.vendor_id
        ? `Vendor #${order.vendor_id}`
        : 'N/A',
    driver_name:
      order.driver?.name || order.driver_id
        ? `Driver #${order.driver_id}`
        : 'Unassigned',
    order_number: order.po_number || order.id,

    // This is the structure expected by AdminOrderDetailsModal
    attachments: {
      pod_photos: pod_photos.map((a) => ({
        ...a,
        url: a.path ? `${BACKEND_URL}/${a.path}` : null,
      })),
      voice_notes: voice_notes.map((a) => ({
        ...a,
        url: a.path ? `${BACKEND_URL}/${a.path}` : null,
      })),
      order_docs: order_docs.map((a) => ({
        ...a,
        url: a.path ? `${BACKEND_URL}/${a.path}` : null,
      })),
    },
  };
};

const mapContainerStyle = {
  height: '400px',
  width: '100%',
};
const defaultCenter = [25.2048, 55.2708];

// Helper functions for the local modal (defined here for a self-contained component)
const getOrderStatusVariant = (status) => {
  switch (status?.toLowerCase()) {
    case 'picked up':
    case 'delivered':
    case 'completed':
      return 'success';
    case 'pending':
      return 'warning';
    case 'in_transit':
    case 'assigned':
    case 'accepted':
      return 'primary';
    case 'cancelled':
    case 'rejected':
    case 'returned':
      return 'danger';
    default:
      return 'secondary';
  }
};

const formatDateTime = (isoString) => {
  if (!isoString) return '—';
  try {
    return new Date(isoString).toLocaleString();
  } catch {
    return 'Invalid Date';
  }
};

// Placeholder for icons if FontAwesome isn't imported
const FaMapPin = ({ className = '' }) => (
  <span className={className} role='img' aria-label='pin'>
    📍
  </span>
);

// Component for Order Details Modal (Local Definition)
// NOTE: This component is currently redundant as the parent uses the imported AdminOrderDetailsModal,
// but kept here for function reuse within the main component's table.
const OrderDetailsModalContent = ({ show, order, handleClose }) => {
  if (!order) return null;

  const handleOpenAttachment = (file) => {
    if (!file.path) return;
    const fileUrl = `${BACKEND_URL}/${file.path}`;
    window.open(fileUrl, '_blank');
  };

  const getPickupAddress = (o) => {
    if (o.type === 'purchase_order') return o.vendor?.address || 'N/A';
    if (o.type === 'sales_order') return o.showroom?.address || 'N/A';
    if (o.type === 'purchase_return') return o.showroom?.address || 'N/A';
    if (o.type === 'sales_return') return o.customer?.address || 'N/A';
    return 'N/A';
  };

  const getDropAddress = (o) => {
    if (o.type === 'purchase_order') return o.showroom?.address || 'N/A';
    if (o.type === 'sales_order') return o.customer?.address || 'N/A';
    if (o.type === 'purchase_return') return o.vendor?.address || 'N/A';
    if (o.type === 'sales_return') return o.showroom?.address || 'N/A';
    return 'N/A';
  };

  // Combining attachments for display
  const attachments = (
    order.attachments
      ? [
          ...(order.attachments.pod_photos || []),
          ...(order.attachments.voice_notes || []),
          ...(order.attachments.order_docs || []),
        ]
      : []
  ).filter((f) => f.url);

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size='lg'
      centered
      className='custom-modal'
    >
      <Modal.Header closeButton className='border-0 pb-0'>
        <div className='d-flex justify-content-between align-items-center w-100'>
          <div>
            <small
              className='text-muted'
              style={{ fontSize: '1.1rem', fontWeight: '600' }}
            >
              Order No: #{order.id}
            </small>
          </div>
          <h4 className='mb-0 flex-grow-1 text-center'>Order Details</h4>
          <div style={{ width: '30px' }}></div>{' '}
          {/* Keeps title perfectly centered */}
        </div>
      </Modal.Header>
      <Modal.Body className='pt-2'>
        <div className='order-detail-card p-4 rounded shadow-sm bg-light'>
          <Row className='g-3'>
            <Col md={6}>
              <div className='detail-item'>
                <strong>Type:</strong>{' '}
                <span className='text-capitalize'>
                  {order.type?.replace(/_/g, ' ') || '—'}
                </span>
              </div>
            </Col>
            <Col md={6}>
              <div className='detail-item'>
                <strong>Customer/Vendor:</strong>{' '}
                {order.customer_name || order.vendor_name || '—'}
              </div>
            </Col>
            <Col md={6}>
              <div className='detail-item'>
                <strong>Pickup:</strong>
                <div className='mt-1'>
                  <FaMapPin className='text-success me-1' />
                  <small>{getPickupAddress(order) || '—'}</small>
                </div>
              </div>
            </Col>
            <Col md={6}>
              <div className='detail-item'>
                <strong>Drop:</strong>
                <div className='mt-1'>
                  <FaMapMarkerAlt className='text-danger me-1' />
                  <small>{getDropAddress(order) || '—'}</small>
                </div>
              </div>
            </Col>
            <Col md={6}>
              <div className='detail-item'>
                <strong>Driver:</strong> {order.driver_name || 'Unassigned'}
              </div>
            </Col>
            <Col md={6}>
              <div className='detail-item'>
                <strong>Status:</strong>
                <Badge
                  bg={getOrderStatusVariant(order.status)}
                  className='ms-2'
                >
                  {order.status?.replace(/_/g, ' ') || '—'}
                </Badge>
              </div>
            </Col>
            <Col md={6}>
              <div className='detail-item'>
                <strong>Created:</strong> {formatDateTime(order.created_at)}
              </div>
            </Col>
            <Col md={6}>
              <div className='detail-item'>
                <strong>Updated:</strong> {formatDateTime(order.updated_at)}
              </div>
            </Col>
            <Col md={12}>
              <div className='detail-item'>
                <strong>Remarks:</strong> {order.remarks || '—'}
              </div>
            </Col>
            <Col md={12}>
              <div className='detail-item'>
                <strong>Attachments:</strong>
                {attachments.length > 0 ? (
                  <div className='d-flex flex-wrap gap-2 mt-2'>
                    {attachments.map((file, idx) => (
                      <Button
                        key={idx}
                        variant='outline-primary'
                        size='sm'
                        onClick={() => handleOpenAttachment(file)}
                        className='d-flex align-items-center gap-1'
                      >
                        <FaBoxOpen />
                        {file.type?.replace(/_/g, ' ') ||
                          file.name ||
                          `File ${idx + 1}`}
                      </Button>
                    ))}
                  </div>
                ) : (
                  <span className='text-muted ms-2'>No files</span>
                )}
              </div>
            </Col>
          </Row>
        </div>
      </Modal.Body>
      <Modal.Footer className='border-0 justify-content-center'>
        <Button variant='secondary' onClick={handleClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const AdminDashboard = () => {
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [summaryError, setSummaryError] = useState(null);
  const [showrooms, setShowrooms] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [allDriversForDropdown, setAllDriversForDropdown] = useState([]); // All drivers for dropdown
  const [orders, setOrders] = useState([]);
  const [collections, setCollections] = useState([]); // State for collections
  const [selectedShowroom, setSelectedShowroom] = useState(null);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [filteredDrivers, setFilteredDrivers] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [filteredCollections, setFilteredCollections] = useState([]); // State for filtered collections

  // New Order Modal States
  const [showNewOrder, setShowNewOrder] = useState(false);
  const [orderType, setOrderType] = useState('');
  const [driverId, setDriverId] = useState('');
  const [vendorAddress, setVendorAddress] = useState('');
  const [dropAddress, setDropAddress] = useState('');

  // Core states used in the API payload
  const [remarks, setRemarks] = useState('');
  const [pickupAddress, setPickupAddress] = useState('');
  const [deliveryAddress, setDeliveryAddress] = useState('');

  // Coordinate states
  const [pickupLat, setPickupLat] = useState('');
  const [pickupLng, setPickupLng] = useState('');
  const [deliveryLat, setDeliveryLat] = useState('');
  const [deliveryLng, setDeliveryLng] = useState('');

  // Order-specific states
  const [poNumbers, setPoNumbers] = useState([]);
  const [poFile, setPoFile] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [showroom, setShowroom] = useState('');
  // Placeholder states for other missing form fields as per logic in the form's reset/change handlers
  const [vendorId, setVendorId] = useState('');
  const [vendors, setVendors] = useState([]);
  const [customerId, setCustomerId] = useState('');
  const [customers, setCustomers] = useState([]);
  const [poNumber, setPoNumber] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [pendingBalance, setPendingBalance] = useState('');
  const [poValue, setPoValue] = useState('');
  const [newFromCoords, setNewFromCoords] = useState(null);
  const [newDropCoords, setNewDropCoords] = useState(null);
  const [filteredShowroomsForDriver, setFilteredShowroomsForDriver] = useState(
    []
  );
  const [selectedPoId, setSelectedPoId] = useState('');
  const [userId, setUserId] = useState(null); // Fetch current user ID for attachment uploads

  // Map Popup States
  const [showNewFromMap, setShowNewFromMap] = useState(false);
  const [showNewDropMap, setShowNewDropMap] = useState(false);

  // Tracking Modal
  const [showTrackModal, setShowTrackModal] = useState(false);
  const [trackDriverId, setTrackDriverId] = useState(null);
  const [trackOrder, setTrackOrder] = useState(null);

  // Grouped showrooms with drivers (only those with drivers)
  const [groupedShowrooms, setGroupedShowrooms] = useState([]);

  // Grouped showrooms with collections
  const [groupedShowroomsCollections, setGroupedShowroomsCollections] =
    useState([]);
  const [selectedShowroomForCollections, setSelectedShowroomForCollections] =
    useState(null);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [showCollectionDetailsModal, setShowCollectionDetailsModal] =
    useState(false);

  // Order Details Modal States
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const [messageModal, setMessageModal] = useState({
    show: false,
    title: '',
    body: '',
  });

  const showMessageModal = (title, body) =>
    setMessageModal({ show: true, title, body });
  const handleCloseMessageModal = () =>
    setMessageModal({ show: false, title: '', body: '' });

  const handleShowOrderModal = (order) => {
    setSelectedOrder(order);
    setShowOrderModal(true);
  };

  const fetchPoNumbers = async () => {
    try {
      const res = await api.get('/purchase-orders');
      setPoNumbers(res.data?.data?.data || []);
    } catch (err) {
      setPoNumbers([]);
    }
  };

  const handlePoSelect = async (selectedPo) => {
    if (!selectedPo) return;
    setSelectedPoId(selectedPo.id);
    setPoNumber(selectedPo.po_number || '');
    if (selectedPo.vendor_id) {
      setVendorId(selectedPo.vendor_id);
      try {
        const res = await api.get(`/vendors/${selectedPo.vendor_id}`);
        const v = res.data?.data;
        if (v) {
          setVendorAddress(v.address || '');
          if (v.lat && v.lng) {
            const lat = parseFloat(v.lat),
              lng = parseFloat(v.lng);
            setPickupLat(lat.toString());
            setPickupLng(lng.toString());
            setNewFromCoords([lat, lng]);
          }
        }
      } catch (err) {
        console.error(err);
      }
    }
    if (selectedPo.showroom_id) {
      setShowroom(selectedPo.showroom_id);
      try {
        const res = await api.get(`/showrooms/${selectedPo.showroom_id}`);
        const s = res.data?.data;
        if (s) {
          setDropAddress(s.address || '');
          if (s.lat && s.lng) {
            const lat = parseFloat(s.lat),
              lng = parseFloat(s.lng);
            setDeliveryLat(lat.toString());
            setDeliveryLng(lng.toString());
            setNewDropCoords([lat, lng]);
          }
        }
      } catch (err) {
        console.error(err);
      }
    }
    if (selectedPo.value) setPoValue(selectedPo.value);
  };

  const handleCustomerSelect = async (selectedCustomer) => {
    if (!selectedCustomer) return;
    setCustomerId(selectedCustomer.id);
    try {
      const res = await api.get(`/orders?customer_id=${selectedCustomer.id}`);
      const orders = res.data?.data?.data || [];
      if (orders.length > 0) {
        const recent = orders[0];
        setVendorAddress(recent.pickup_address || '');
        setDropAddress(recent.delivery_address || '');
        if (recent.pickup_lat && recent.pickup_lng) {
          setPickupLat(recent.pickup_lat);
          setPickupLng(recent.pickup_lng);
        }
        if (recent.delivery_lat && recent.delivery_lng) {
          setDeliveryLat(recent.delivery_lat);
          setDeliveryLng(recent.delivery_lng);
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleShowroomSelect = async (selectedShowroom) => {
    if (!selectedShowroom) return;
    setShowroom(selectedShowroom.id);
    setDropAddress(selectedShowroom.address || '');
    if (selectedShowroom.lat && selectedShowroom.lng) {
      const lat = parseFloat(selectedShowroom.lat),
        lng = parseFloat(selectedShowroom.lng);
      setDeliveryLat(lat.toString());
      setDeliveryLng(lng.toString());
      setNewDropCoords([lat, lng]);
    }
  };

  const fetchVendors = async () => {
    try {
      const res = await api.get('/vendors');
      setVendors(Array.isArray(res.data?.data?.data) ? res.data.data.data : []);
    } catch (err) {
      setVendors([]);
    }
  };

  // ✅ NEW: Function to fetch customers
  const fetchCustomers = async () => {
    try {
      const res = await api.get('/customers'); // Assuming '/customers' is the correct endpoint
      setCustomers(
        Array.isArray(res.data?.data?.data) ? res.data.data.data : []
      );
    } catch (err) {
      setCustomers([]);
    }
  };

  const handleVendorSelect = async (selectedVendor) => {
    if (!selectedVendor) return;
    setVendorId(selectedVendor.id);
    setVendorAddress(selectedVendor.address || '');
    if (selectedVendor.lat && selectedVendor.lng) {
      const lat = parseFloat(selectedVendor.lat),
        lng = parseFloat(selectedVendor.lng);
      setPickupLat(lat.toString());
      setPickupLng(lng.toString());
      setNewFromCoords([lat, lng]);
    }
  };

  const handleCloseOrderModal = () => {
    setShowOrderModal(false);
    setSelectedOrder(null);
  };

  // --- NEW ADDRESS HELPER FUNCTIONS ---
  // These functions implement the logic requested for the table cell
  const getPickupAddress = (order) => {
    if (order.type === 'purchase_order') return order.vendor?.address || 'N/A';
    if (order.type === 'sales_order') return order.showroom?.address || 'N/A';
    if (order.type === 'purchase_return')
      return order.showroom?.address || 'N/A';
    if (order.type === 'sales_return') return order.customer?.address || 'N/A';
    return 'N/A';
  };

  const getDropAddress = (order) => {
    if (order.type === 'purchase_order')
      return order.showroom?.address || 'N/A';
    if (order.type === 'sales_order') return order.customer?.address || 'N/A';
    if (order.type === 'purchase_return') return order.vendor?.address || 'N/A';
    if (order.type === 'sales_return') return order.showroom?.address || 'N/A';
    return 'N/A';
  };
  // -------------------------------------

  // Helper to fetch all paginated orders from the API
  const fetchAllOrdersFromApi = async (params = {}) => {
    const allOrders = [];
    let page = 1;
    const maxPages = 200; // safety cap to avoid infinite loops

    while (page <= maxPages) {
      try {
        const res = await api.get('/orders', { params: { ...params, page } });
        const body = res.data;

        // Normalize to an array of orders from possible response shapes
        let pageOrders = [];
        if (body?.data?.data) pageOrders = body.data.data;
        else if (Array.isArray(body?.data)) pageOrders = body.data;
        else if (Array.isArray(body)) pageOrders = body;

        if (!Array.isArray(pageOrders)) pageOrders = [];

        allOrders.push(...pageOrders);

        // If paginator meta exists, use it to stop
        const meta = body?.data?.meta;
        if (meta) {
          if (meta.current_page >= meta.last_page) break;
          page = meta.current_page + 1;
          continue;
        }

        // If no meta, stop when the current page returned fewer than expected items
        if (pageOrders.length === 0) break;
        // Heuristic: if fewer than 20 items, likely last page
        if (pageOrders.length < 20) break;

        page += 1;
      } catch (err) {
        // Bubble up the error so callers can handle it (Promise.allSettled will mark as rejected)
        throw err;
      }
    }

    // Return an axios-like response object so existing parsing logic works
    return { data: { data: allOrders } };
  };

  const fetchAllDriversFromApi = async (params = {}) => {
    const allDrivers = [];
    let page = 1;
    const maxPages = 200; // safety cap to avoid infinite loops

    while (page <= maxPages) {
      try {
        const res = await api.get('/drivers', {
          params: { ...params, page, per_page: 20 },
        });
        const body = res.data;

        // Normalize to an array of drivers from possible response shapes
        let pageDrivers = [];
        if (body?.data?.data) pageDrivers = body.data.data;
        else if (Array.isArray(body?.data)) pageDrivers = body.data;
        else if (Array.isArray(body)) pageDrivers = body;

        if (!Array.isArray(pageDrivers)) pageDrivers = [];

        allDrivers.push(...pageDrivers);

        // If paginator meta exists, use it to stop
        const meta = body?.data?.meta;
        if (meta) {
          // Check if we've reached the total count or last page
          if (meta.current_page >= meta.last_page) break;
          if (allDrivers.length >= meta.total) break;
          page = meta.current_page + 1;
          continue;
        }

        // If response has total and we've reached it, stop
        if (body?.data?.total && allDrivers.length >= body.data.total) break;

        // If no meta, stop when the current page returned fewer than expected items
        if (pageDrivers.length === 0) break;
        // Heuristic: if fewer than 20 items, likely last page
        if (pageDrivers.length < 20) break;

        page += 1;
      } catch (err) {
        // Bubble up the error so callers can handle it (Promise.allSettled will mark as rejected)
        throw err;
      }
    }

    // Return an axios-like response object so existing parsing logic works
    return { data: { data: allDrivers } };
  };

  // 🌟 UPDATED: fetchAllData to map attachments structure 🌟
  const fetchAllData = useCallback(async () => {
    setLoading(true);
    try {
      const userShowroomId = localStorage.getItem('userShowroomId');

      // Step 1: Fetch orders with statuses: assigned, in_transit, picked_up
      const ordersWithStatuses = [];
      const statuses = ['assigned', 'in_transit', 'picked_up'];

      // Fetch orders for each status
      for (const status of statuses) {
        try {
          const statusOrdersRes = await fetchAllOrdersFromApi({
            showroom_id: userShowroomId,
            status: status,
          });
          let statusOrderData = statusOrdersRes.data;
          if (statusOrderData?.data?.data)
            statusOrderData = statusOrderData.data.data;
          else if (statusOrderData?.data)
            statusOrderData = statusOrderData.data;
          else if (!Array.isArray(statusOrderData)) statusOrderData = [];

          // Map attachments and add to orders array
          const mappedStatusOrders = statusOrderData.map((order) =>
            mapOrderAttachments(order)
          );
          ordersWithStatuses.push(...mappedStatusOrders);
        } catch (err) {
          console.error(`Error fetching orders with status ${status}:`, err);
        }
      }

      // Step 2: Extract unique driver IDs from orders
      const driverIds = new Set();
      ordersWithStatuses.forEach((order) => {
        if (order.driver_id) {
          driverIds.add(order.driver_id);
        }
      });

      // Step 3: Fetch only the drivers that have orders with these statuses
      let relevantDrivers = [];
      if (driverIds.size > 0) {
        // Fetch drivers by IDs (if API supports it) or fetch all and filter
        try {
          // Try to fetch specific drivers by ID
          const driverIdsArray = Array.from(driverIds);
          const driversRes = await fetchAllDriversFromApi({
            showroom_id: userShowroomId,
          });
          let allDriversData = driversRes.data;
          if (allDriversData?.data?.data)
            allDriversData = allDriversData.data.data;
          else if (allDriversData?.data) allDriversData = allDriversData.data;
          else if (!Array.isArray(allDriversData)) allDriversData = [];

          // Filter to only include drivers with orders
          relevantDrivers = allDriversData.filter((driver) =>
            driverIdsArray.includes(driver.id)
          );
        } catch (err) {
          console.error('Error fetching drivers:', err);
        }
      }

      // Step 4: Fetch other data in parallel
      // Fetch collections with status filter: assigned_to_driver, collected, submitted, verified
      const collectionStatuses = [
        'assigned_to_driver',
        'collected',
        'submitted',
        'verified',
      ];
      const collectionsPromises = collectionStatuses.map((status) =>
        api.get('/collections', {
          params: {
            status: status,
          },
        })
      );

      // Fetch all drivers for dropdown (without filtering)
      try {
        const allDriversRes = await fetchAllDriversFromApi({});
        let allDriversData = allDriversRes.data;
        if (allDriversData?.data?.data)
          allDriversData = allDriversData.data.data;
        else if (allDriversData?.data) allDriversData = allDriversData.data;
        else if (!Array.isArray(allDriversData)) allDriversData = [];
        setAllDriversForDropdown(allDriversData);
      } catch (err) {
        console.error('Error fetching all drivers for dropdown:', err);
        setAllDriversForDropdown([]);
      }

      const [summaryRes, showroomsRes, userRes, ...collectionsResArray] =
        await Promise.allSettled([
          api.get('/dashboard/admin-summary', {
            params: { showroom_id: userShowroomId },
          }),
          api.get('/showrooms'),
          api.get('/user'), // Fetch user ID
          ...collectionsPromises, // Fetch collections for each status
        ]);

      if (userRes.status === 'fulfilled') {
        setUserId(userRes.value.data.id);
      } else {
        console.error('Failed to load user ID:', userRes.reason);
      }

      if (summaryRes.status === 'fulfilled') {
        const summaryData =
          summaryRes.value.data?.data || summaryRes.value.data;
        setSummary(summaryData);
      } else {
        setSummaryError('Failed to load summary data.');
        console.error('Summary error:', summaryRes.reason);
      }

      // Set drivers (only those with orders with specified statuses)
      setDrivers(relevantDrivers);

      // Set orders (only those with specified statuses)
      setOrders(ordersWithStatuses);

      if (showroomsRes.status === 'fulfilled') {
        let showroomData = showroomsRes.value.data;
        if (showroomData?.data?.data) showroomData = showroomData.data.data;
        else if (showroomData?.data) showroomData = showroomData.data;
        else if (!Array.isArray(showroomData)) showroomData = [];
        setShowrooms(showroomData);
      } else {
        console.error('Failed to load showrooms:', showroomsRes.reason);
      }

      // Combine collections from all status filters
      const allCollections = [];
      collectionsResArray.forEach((collectionRes, index) => {
        if (collectionRes.status === 'fulfilled') {
          // Handle new API response structure: { success: true, data: [...] }
          const rawData = collectionRes.value.data;
          const collectionData =
            rawData?.success && Array.isArray(rawData?.data)
              ? rawData.data
              : rawData?.data?.data ??
                rawData?.data ??
                (Array.isArray(rawData) ? rawData : []);

          if (Array.isArray(collectionData)) {
            allCollections.push(...collectionData);
          } else {
            console.error(
              `Received non-array data for collections with status ${collectionStatuses[index]}:`,
              collectionData
            );
          }
        } else {
          console.error(
            `Failed to load collections with status ${collectionStatuses[index]}:`,
            collectionRes.reason
          );
        }
      });

      // Remove duplicates based on collection ID
      const uniqueCollections = Array.from(
        new Map(allCollections.map((c) => [c.id, c])).values()
      );

      setCollections(uniqueCollections);
    } catch (err) {
      console.error('Overall data fetching error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllData();
    // ✅ Call the fetch functions when the component mounts
    fetchVendors();
    fetchCustomers();
    fetchPoNumbers(); // Also ensuring POs are loaded
  }, [fetchAllData]);

  useEffect(() => {
    const role = localStorage.getItem('userRole');
    setUserRole(role);
  }, []);

  // Effect to filter showrooms based on selected driver
  useEffect(() => {
    if (driverId) {
      const selectedDriverObj = drivers.find(
        (d) => d.id === parseInt(driverId)
      );
      // The API response for drivers includes a 'showroom' array.
      const driverShowrooms = selectedDriverObj?.showroom || [];
      setFilteredShowroomsForDriver(driverShowrooms);
    } else {
      // If no driver is selected, show all showrooms.
      setFilteredShowroomsForDriver(showrooms);
    }
    setShowroom(''); // Reset selected showroom when driver changes
  }, [fetchAllData]);

  // Effect to filter showrooms based on selected driver
  // Update the dependencies of this useEffect to trigger when driverId, drivers, or showrooms change.
  useEffect(() => {
    // Convert driverId from string to number for comparison
    const selectedDriverId = parseInt(driverId);

    if (selectedDriverId) {
      const selectedDriverObj = drivers.find((d) => d.id === selectedDriverId);

      if (selectedDriverObj) {
        // Assuming driver.showroom_id is an array of IDs, or a single ID/JSON string
        let driverShowroomIds = [];
        if (Array.isArray(selectedDriverObj.showroom_id)) {
          driverShowroomIds = selectedDriverObj.showroom_id;
        } else if (typeof selectedDriverObj.showroom_id === 'string') {
          // Safely parse JSON string of IDs
          try {
            const parsedIds = JSON.parse(selectedDriverObj.showroom_id);
            if (Array.isArray(parsedIds)) {
              driverShowroomIds = parsedIds;
            }
          } catch (e) {
            console.error('Failed to parse showroom_id string:', e);
          }
        } else if (typeof selectedDriverObj.showroom_id === 'number') {
          driverShowroomIds = [selectedDriverObj.showroom_id];
        }

        // Ensure IDs are treated as numbers for comparison
        const numericDriverShowroomIds = driverShowroomIds.map(Number);

        const filtered = showrooms.filter((s) =>
          numericDriverShowroomIds.includes(s.id)
        );
        setFilteredShowroomsForDriver(filtered);
      } else {
        setFilteredShowroomsForDriver([]); // Driver not found, no showrooms
      }
    } else {
      // If no driver is selected (driverId is empty), show all showrooms.
      setFilteredShowroomsForDriver(showrooms);
    }
    // Note: setShowroom('') should be handled in the onChange for driverId, which you already have.
    // setShowroom('');

    // DEPENDENCY FIX: Now correctly listening to state changes
  }, [driverId, drivers, showrooms]);

  // NOTE: The duplicated useEffect (lines 680-692 and 695-717) should be merged/replaced by the one above.

  // Group drivers by showroom_id (cast as array)
  useEffect(() => {
    if (drivers.length === 0) {
      setGroupedShowrooms([]);
      return;
    }

    const showroomMap = new Map();
    showrooms.forEach((s) => showroomMap.set(s.id, s));

    const groups = [];

    drivers.forEach((driver) => {
      let showroomIds = [];
      if (Array.isArray(driver.showroom_id)) {
        showroomIds = driver.showroom_id;
      } else if (typeof driver.showroom_id === 'string') {
        try {
          showroomIds = JSON.parse(driver.showroom_id);
          if (!Array.isArray(showroomIds)) showroomIds = [];
        } catch {
          showroomIds = [];
        }
      }

      if (showroomIds.length > 0) {
        showroomIds.forEach((id) => {
          if (showroomMap.has(id)) {
            const showroom = showroomMap.get(id);
            const existing = groups.find((g) => g.id === id);
            if (existing) {
              existing.drivers.push(driver);
            } else {
              groups.push({ ...showroom, drivers: [driver] });
            }
          }
        });
      } else {
        // Unassigned
        const unassigned = groups.find((g) => g.id === 'unassigned');
        if (unassigned) {
          unassigned.drivers.push(driver);
        } else {
          groups.push({
            id: 'unassigned',
            name: 'Unassigned Drivers',
            code: 'UNASSIGNED',
            drivers: [driver],
          });
        }
      }
    });

    setGroupedShowrooms(groups);
  }, [drivers, showrooms]);

  // Group collections by showroom_id
  useEffect(() => {
    if (collections.length === 0) {
      setGroupedShowroomsCollections([]);
      return;
    }

    const showroomMap = new Map();
    showrooms.forEach((s) => showroomMap.set(s.id, s));

    const groups = [];

    collections.forEach((collection) => {
      // Handle both old and new API structure field names
      const showroomId = collection.showroom_id || collection.showroomId;

      if (showroomId && showroomMap.has(showroomId)) {
        const showroom = showroomMap.get(showroomId);
        const existing = groups.find((g) => g.id === showroomId);
        if (existing) {
          existing.collections.push(collection);
        } else {
          groups.push({ ...showroom, collections: [collection] });
        }
      } else {
        // Unassigned
        const unassigned = groups.find((g) => g.id === 'unassigned');
        if (unassigned) {
          unassigned.collections.push(collection);
        } else {
          groups.push({
            id: 'unassigned',
            name: 'Unassigned Collections',
            code: 'UNASSIGNED',
            collections: [collection],
          });
        }
      }
    });

    setGroupedShowroomsCollections(groups);
  }, [collections, showrooms]);

  // Update filtered drivers
  useEffect(() => {
    if (selectedShowroom && groupedShowrooms.length > 0) {
      const grp = groupedShowrooms.find((g) => g.id === selectedShowroom.id);
      setFilteredDrivers(grp?.drivers ?? []);
    } else {
      setFilteredDrivers([]);
    }
  }, [selectedShowroom, groupedShowrooms]);

  // Filter orders
  useEffect(() => {
    if (selectedDriver) {
      // Filter orders for the selected driver
      const driverOrders = orders.filter(
        (o) =>
          o.driver_id === selectedDriver.id &&
          ['assigned', 'accepted', 'picked_up', 'in_transit'].includes(o.status)
      );
      setFilteredOrders(driverOrders);

      // Filter collections for the selected driver
      const driverCollections = collections.filter(
        (c) =>
          c.assigned_to === selectedDriver.id &&
          ['assigned', 'assigned_to_driver'].includes(c.status)
      );
      setFilteredCollections(driverCollections);
    } else {
      // If no driver is selected, clear both lists
      setFilteredOrders([]);
      setFilteredCollections([]);
    }
  }, [selectedDriver, orders, collections]);

  const selectShowroomForCollections = (showroom) => {
    // Check if the clicked showroom is already selected
    if (
      selectedShowroomForCollections &&
      selectedShowroomForCollections.id === showroom.id
    ) {
      setSelectedShowroomForCollections(null); // Deselect (close the section)
      setSelectedCollection(null); // Clear selected collection
    } else {
      setSelectedShowroomForCollections(showroom); // Select the new showroom (open the section)
      setSelectedCollection(null); // Clear previous selected collection
    }
  };

  const selectCollection = (collection) => {
    setSelectedCollection(collection);
    setShowCollectionDetailsModal(true);
  };

  const handleCloseCollectionDetails = () => {
    setShowCollectionDetailsModal(false);
    setSelectedCollection(null);
  };

  const selectShowroom = (showroom) => {
    // Check if the clicked showroom is already selected
    if (selectedShowroom && selectedShowroom.id === showroom.id) {
      setSelectedShowroom(null); // Deselect (close the section)
      setSelectedDriver(null); // Clear selected driver
      setFilteredOrders([]);
    } else {
      setSelectedShowroom(showroom); // Select the new showroom (open the section)
      setSelectedDriver(null); // Clear previous selected driver
      setFilteredOrders([]);
      setFilteredCollections([]);
    }
  };

  const selectDriver = (driver) => {
    setSelectedDriver(driver);
  };

  const handleTrackDriver = (order) => {
    setTrackDriverId(order.driver_id || null);
    setTrackOrder(order);
    setShowTrackModal(true);
  };

  const handleCloseTrackModal = () => {
    setShowTrackModal(false);
    setTrackDriverId(null);
    setTrackOrder(null);
  };

  const handleNewOrderChange = (e) => {
    const { name, value } = e.target;
    if (name === 'orderType') setOrderType(value);
    else if (name === 'driverId') {
      setDriverId(value);
      setShowroom('');
    } // Reset showroom when driver changes
    else if (name === 'pickupAddress') setPickupAddress(value);
    else if (name === 'deliveryAddress') setDeliveryAddress(value);
    else if (name === 'pickupLat') setPickupLat(value);
    else if (name === 'pickupLng') setPickupLng(value);
    else if (name === 'deliveryLat') setDeliveryLat(value);
    else if (name === 'deliveryLng') setDeliveryLng(value);
    else if (name === 'remarks') setRemarks(value);
    // Correctly handling the input fields in the modal
    else if (name === 'vendorAddress')
      setVendorAddress(value); // Binds to Pickup Location text area
    else if (name === 'dropAddress')
      setDropAddress(value); // Binds to Drop Location text area
    // Additional missing state variables from the form's logic:
    else if (name === 'showroom') setShowroom(value);
    else if (name === 'poNumber') setPoNumber(value);
    else if (name === 'invoiceNumber') setInvoiceNumber(value);
    else if (name === 'pendingBalance') setPendingBalance(value);
    else if (name === 'poValue') setPoValue(value);
    else if (name === 'customerId') setCustomerId(value);
    else if (name === 'vendorId') setVendorId(value);
  };

  const handleFileChange = (e) => {
    if (e.target.files?.[0]) setPoFile(e.target.files[0]);
  };

  const handleSetFromLocation = (coords, address) => {
    setPickupLat(coords[0].toString());
    setPickupLng(coords[1].toString());
    // Set the form field state for visual feedback
    setVendorAddress(address);
    // Also set the payload state if needed, though only vendorAddress is used below
    setPickupAddress(address);
    setShowNewFromMap(false);
  };

  const handleSetDropLocation = (coords, address) => {
    setDeliveryLat(coords[0].toString());
    setDeliveryLng(coords[1].toString());
    // Set the form field state for visual feedback
    setDropAddress(address);
    // Also set the payload state if needed, though only dropAddress is used below
    setDeliveryAddress(address);
    setShowNewDropMap(false);
  };

  const handleSubmitNewOrder = async (e) => {
    e.preventDefault();
    setSubmitLoading(true);
    setSubmitError(null);

    // Use vendorAddress and dropAddress in the payload as they are the source of truth from the form fields
    const finalPickupAddress = vendorAddress;
    const finalDeliveryAddress = dropAddress;

    try {
      const currentUserId =
        userId || Number(localStorage.getItem('userId') || '0');
      const payload = {
        type: orderType,
        // Don't include driver_id in initial creation - will assign separately if driver is selected
        pickup_address: finalPickupAddress,
        delivery_address: finalDeliveryAddress,
        pickup_lat: pickupLat ? parseFloat(pickupLat) : null,
        pickup_lng: pickupLng ? parseFloat(pickupLng) : null,
        delivery_lat: deliveryLat ? parseFloat(deliveryLat) : null,
        delivery_lng: deliveryLng ? parseFloat(deliveryLng) : null,
        remarks,
        // Include other relevant fields like showroom_id if needed in API
        showroom_id: showroom ? parseInt(showroom) : null,
        po_number: poNumber, // Include PO number
        invoice_number: invoiceNumber, // Include Invoice number
        pending_balance: pendingBalance ? parseFloat(pendingBalance) : null,
        po_value: poValue ? parseFloat(poValue) : null,
        customer_id: customerId ? parseInt(customerId) : null,
        vendor_id: vendorId ? parseInt(vendorId) : null,
      };

      // Basic validation to prevent submission if addresses are missing
      if (!finalPickupAddress || !finalDeliveryAddress) {
        throw new Error('Please enter both Pickup and Drop locations.');
      }

      const { data } = await api.post('/orders', payload);
      const newOrder = data.data;

      // Use the status-aware attachment upload function
      if (poFile && newOrder.id)
        await uploadAttachment(newOrder.id, poFile, currentUserId, 'pending');

      // If driver is selected, assign driver using the assign driver API
      if (driverId && newOrder.id) {
        try {
          await api.post(`/orders/${newOrder.id}/assign-driver`, {
            driver_id: parseInt(driverId),
          });
        } catch (assignErr) {
          console.error('Error assigning driver:', assignErr);
          // Show warning but don't fail the entire order creation
          showMessageModal(
            'Warning',
            'Order created successfully, but driver assignment failed. Please assign driver manually.'
          );
          // Still continue with success flow
          setShowNewOrder(false);
          fetchAllData();
          resetNewOrderForm();
          return;
        }
      }

      setShowNewOrder(false);
      const successMessage = driverId
        ? 'Order created and driver assigned successfully!'
        : 'Order created successfully!';
      showMessageModal('Success', successMessage);

      // Re-fetch data to update dashboard view
      fetchAllData();
      resetNewOrderForm();
    } catch (err) {
      const errorMessage =
        err.message || err.response?.data?.message || 'Failed to create order.';
      setSubmitError(errorMessage);
      showMessageModal('Error', `Order creation failed: ${errorMessage}`); // Added error message
      console.error('Order creation error:', err);
    } finally {
      setSubmitLoading(false);
    }
  };

  const resetNewOrderForm = () => {
    setOrderType('');
    setDriverId(''); // Reset driver ID
    setPickupAddress(''); // Clear old address state
    setDeliveryAddress(''); // Clear old address state
    setVendorAddress(''); // Clear form address state
    setDropAddress(''); // Clear form address state
    setPickupLat('');
    setPickupLng('');
    setDeliveryLat('');
    setDeliveryLng('');
    setRemarks('');
    setPoFile(null);
    setShowroom(''); // Reset showroom
    setVendorId('');
    setCustomerId('');
    setPoNumber('');
    setInvoiceNumber('');
    setPendingBalance('');
    setPoValue('');
    setNewFromCoords(null);
    setNewDropCoords(null);
    setSelectedPoId('');
  };

  // Helper function to handle conditional fields (required by the original modal structure)
  const getNewOrderFields = () => {
    switch (orderType) {
      case 'purchase_order':
        return (
          <>
            <Row className='mb-3'>
              <Col md={6}>
                <Form.Group controlId='selectVendor'>
                  <Form.Label>Vendor</Form.Label>
                  <Form.Select
                    value={vendorId}
                    onChange={(e) => {
                      setVendorId(e.target.value);
                      const v = vendors.find(
                        (vv) => vv.id === parseInt(e.target.value)
                      );
                      handleVendorSelect(v);
                    }}
                  >
                    <option value=''>Select Vendor</option>
                    {vendors.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId='poNumber'>
                  <Form.Label>PO #</Form.Label>
                  <Form.Control
                    type='text'
                    value={poNumber}
                    onChange={(e) => setPoNumber(e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className='mb-3' controlId='poValue'>
              <Form.Label>PO Value</Form.Label>
              <Form.Control
                type='number'
                value={poValue}
                onChange={(e) => setPoValue(e.target.value)}
              />
            </Form.Group>
            <Form.Group className='mb-3' controlId='attachPO'>
              <Form.Label>Attach PO</Form.Label>
              <Form.Control
                type='file'
                accept='image/*,application/pdf'
                onChange={(e) => setPoFile(e.target.files[0])}
              />
            </Form.Group>
          </>
        );
      case 'purchase_return':
        return (
          <>
            <Row className='mb-3'>
              <Col md={6}>
                <Form.Group controlId='selectVendor'>
                  <Form.Label>Vendor</Form.Label>
                  <Form.Select
                    value={vendorId}
                    onChange={(e) => {
                      setVendorId(e.target.value);
                      const v = vendors.find(
                        (vv) => vv.id === parseInt(e.target.value)
                      );
                      handleVendorSelect(v);
                    }}
                  >
                    <option value=''>Select Vendor</option>
                    {vendors.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId='poNumber'>
                  <Form.Label>PO #</Form.Label>
                  <Form.Select
                    value={selectedPoId}
                    onChange={(e) => {
                      const po = poNumbers.find(
                        (p) => p.id === parseInt(e.target.value)
                      );
                      handlePoSelect(po);
                    }}
                  >
                    <option value=''>Select PO</option>
                    {poNumbers.map((po) => (
                      <option key={po.id} value={po.id}>
                        {po.po_number}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className='mb-3' controlId='poValue'>
              <Form.Label>PO Value</Form.Label>
              <Form.Control
                type='number'
                value={poValue}
                onChange={(e) => setPoValue(e.target.value)}
              />
            </Form.Group>
            <Form.Group className='mb-3' controlId='attachPO'>
              <Form.Label>Attach PO</Form.Label>
              <Form.Control
                type='file'
                accept='image/*,application/pdf'
                onChange={(e) => setPoFile(e.target.files[0])}
              />
            </Form.Group>
          </>
        );
      case 'sales_return':
        return (
          <>
            <Row className='mb-3'>
              <Col md={6}>
                <Form.Group controlId='selectCustomer'>
                  <Form.Label>Customer</Form.Label>
                  <Form.Select
                    value={customerId}
                    onChange={(e) => {
                      setCustomerId(e.target.value);
                      const c = customers.find(
                        (cc) => cc.id === parseInt(e.target.value)
                      );
                      handleCustomerSelect(c);
                    }}
                  >
                    <option value=''>Select Customer</option>
                    {customers.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId='invoiceNumber'>
                  <Form.Label>Invoice Number</Form.Label>
                  <Form.Control
                    type='text'
                    value={invoiceNumber}
                    onChange={(e) => setInvoiceNumber(e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>
          </>
        );
      case 'payment_collection':
        return (
          <>
            <Row className='mb-3'>
              <Col md={6}>
                <Form.Group controlId='selectCustomer'>
                  <Form.Label>Customer</Form.Label>
                  <Form.Select
                    value={customerId}
                    onChange={(e) => {
                      setCustomerId(e.target.value);
                      const c = customers.find(
                        (cc) => cc.id === parseInt(e.target.value)
                      );
                      handleCustomerSelect(c);
                    }}
                  >
                    <option value=''>Select Customer</option>
                    {customers.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId='pendingBalance'>
                  <Form.Label>Pending Balance</Form.Label>
                  <Form.Control
                    type='number'
                    value={pendingBalance}
                    onChange={(e) => setPendingBalance(e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>
          </>
        );
      default:
        return null;
    }
  };

  // New wrapper function to call the existing submit logic
  const handleSubmitOrder = (e) => {
    handleSubmitNewOrder(e);
  };

  // MapPopup Component
  const MapPopup = ({
    show,
    handleClose,
    title,
    center,
    setCoordinatesAndAddress,
  }) => {
    const [pos, setPos] = useState(null);
    const [addr, setAddr] = useState('');
    const click = async (e) => {
      const { lat, lng } = e.latlng;
      setPos([lat, lng]);
      try {
        const r = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`
        );
        const d = await r.json();
        setAddr(d.display_name || '');
      } catch {
        setAddr('Address not found');
      }
    };
    const MapEvents = () => {
      useMapEvents({ click });
      return null;
    };
    const confirm = () => {
      if (pos && addr) {
        setCoordinatesAndAddress(pos, addr);
        handleClose();
      }
    };
    if (!show) return null;
    return (
      <Modal show={show} onHide={handleClose} size='lg'>
        <Modal.Header closeButton>
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={mapContainerStyle}>
            <MapContainer
              center={center}
              zoom={13}
              style={{ height: '100%', width: '100%' }}
            >
              <TileLayer
                url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
                attribution='© <a href="https://openstreetmap.org">OpenStreetMap</a>'
              />
              <SearchBar onSearchSelect={(l) => setPos([l.y, l.x])} />
              <MapEvents />
              {pos && (
                <Marker position={pos} icon={pickupIcon}>
                  <Popup>{addr || 'Loading...'}</Popup>
                </Marker>
              )}
            </MapContainer>
          </div>
          {pos && (
            <Alert variant='info' className='mt-3'>
              <strong>Selected Address:</strong> {addr || 'Resolving...'}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={handleClose}>
            Cancel
          </Button>
          <Button variant='primary' onClick={confirm} disabled={!pos}>
            Confirm Location
          </Button>
        </Modal.Footer>
      </Modal>
    );
  };

  return (
    <div className='border p-2 rounded' style={{ background: '#fff' }}>
      <Container fluid className='p-4'>
        <Modal
          show={messageModal.show}
          onHide={handleCloseMessageModal}
          centered
          size='md'
        >
          <Modal.Body
            className='py-4 text-center'
            style={{
              backgroundColor: '#fff',
              color: '#1f1f1f',
              borderRadius: '15px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
            }}
          >
            <div className='mb-3'>
              {messageModal.title === 'Success' ? (
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='60'
                  height='60'
                  fill='green'
                  className='bi bi-check-circle-fill'
                  viewBox='0 0 16 16'
                >
                  <path d='M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM6.97 11.03a.75.75 0 0 0 1.08 0l3.992-3.992a.75.75 0 0 0-1.08-1.04L7.5 9.439 5.53 7.47a.75.75 0 0 0-1.08 1.04l2.52 2.52z' />
                </svg>
              ) : (
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='60'
                  height='60'
                  fill='red'
                  className='bi bi-x-circle-fill'
                  viewBox='0 0 16 16'
                >
                  <path d='M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM4.646 4.646a.5.5 0 0 0 0 .708L7.293 8l-2.647 2.646a.5.5 0 0 0 .708.708L8 8.707l2.646 2.647a.5.5 0 0 0 .708-.708L8.707 8l2.647-2.646a.5.5 0 0 0-.708-.708L8 7.293 5.354 4.646a.5.5 0 0 0-.708 0z' />
                </svg>
              )}
            </div>
            <h5 className='mb-2'>{messageModal.title}</h5>
            <p>{messageModal.body}</p>
            <Button
              variant={messageModal.title === 'Success' ? 'success' : 'danger'}
              style={{ width: '100%' }}
              onClick={handleCloseMessageModal}
            >
              Close
            </Button>
          </Modal.Body>
        </Modal>
        <h3 style={{ textAlign: 'center' }}>
          {userRole === 'logistics_manager'
            ? 'Logistics Dashboard'
            : 'Admin Dashboard'}
        </h3>
        <style type='text/css'>{`
                body { background-color: #f4f6f9; }
                .dashboard-card { 
                    background: #b8dbe9;
                    border-radius: 12px;
                    padding: 20px;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
                    transition: transform 0.2s, box-shadow 0.2s;
                    border: none;
                }
                .dashboard-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
                }
                .dashboard-card-header {
                    width: 100%;
                }
                .dashboard-card-icon {
                    width: 40px;
                    height: 40px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 18px;
                    color: white;
                    flex-shrink: 0;
                }
                .dashboard-card-label {
                    color: #000;
                    font-size: 13px;
                    font-weight: 500;
                }
                .dashboard-card-value {
                    font-size: 20px;
                    font-weight: 700;
                    color: #1f2937;
                    margin: 0;
                    line-height: 1.2;
                }
                .dashboard-card-chevron {
                    color: #9ca3af;
                    font-size: 12px;
                }
                .new-order-btn {
                    background: linear-gradient(135deg, #3b82f6, #2563eb);
                    border: none;
                    border-radius: 8px;
                    padding: 8px 20px;
                    font-weight: 600;
                    font-size: 14px;
                    color: white;
                    box-shadow: 0 2px 6px rgba(59, 130, 246, 0.3);
                    transition: all 0.2s ease;
                }
                .new-order-btn:hover {
                    background: linear-gradient(135deg, #2563eb, #1d4ed8);
                    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
                    transform: translateY(-1px);
                    color: white;
                }
                .new-order-btn:active {
                    transform: translateY(0);
                    box-shadow: 0 2px 6px rgba(59, 130, 246, 0.3);
                }
                .showroom-header { cursor:pointer;background:#f8f9fa;padding:10px 15px;border-radius:8px;margin-bottom:8px;transition:background .2s;border:1px solid #dee2e6; }
                .showroom-header:hover { background:#e9ecef; }
                .showroom-header.selected { background: linear-gradient(to bottom, #298df7, #134882)
color:white; }
                .driver-avatar { width:35px;height:35px;border-radius:50%;background:linear-gradient(to bottom, #298df7, #134882);color:#fff;display:inline-flex;align-items:center;justify-content:center;font-weight:bold;margin-right:10px; }
                .driver-list-item { display:flex;align-items:center;justify-content:space-between;padding:8px 12px;border-bottom:1px solid #eee;background:#fff;border-radius:6px;margin-bottom:6px;cursor:pointer;transition:all .2s; }
                .driver-list-item:hover { background:#f1f1f1; }
                .driver-list-item.selected { background:#e7f3ff;border-left:4px solid #007bff;box-shadow:0 1px 3px rgba(0,0,0,.1); }
                .driver-name { display:flex;align-items:center; }
                .count-badge { background:#ccc;border-radius:50%;font-size:.75rem;padding:4px 8px;color:#000;min-width:28px;text-align:center; }
                table th { background:#004aad;color:white;font-weight:500; }
                .status-badge { background:#007bff;color:white;padding:3px 8px;border-radius:5px;font-size:.8rem;font-weight:500; }
                .details-row { display:none;background:#f9f9f9; }
                .details-cell { padding:10px 20px;font-size:.9rem;color:#333; }
                .debug-info { font-family:monospace;font-size:.8rem;color:#666;background:#f8f9fa;padding:8px;border-radius:4px;margin-top:10px; }
            `}</style>

        <div className='container-fluid mt-3'>
          {loading ? (
            <div className='text-center py-5'>
              <Spinner animation='border' variant='primary' />
              <p className='mt-2'>Loading dashboard...</p>
            </div>
          ) : (
            <>
              {/* Dashboard Summary Cards */}
              <Row className='g-3 mb-4'>
                {[
                  {
                    label: 'Total Orders',
                    value: summary?.total_orders ?? 0,
                    icon: FaShoppingCart,
                    iconColor: '#3b82f6',
                    iconBg: '#3b82f6',
                  },
                  {
                    label: 'Pending (Today)',
                    value: summary?.pending_today ?? 0,
                    icon: FaClock,
                    iconColor: '#10b981',
                    iconBg: '#10b981',
                  },
                  {
                    label: 'Pending',
                    value: summary?.pending ?? 0,
                    icon: FaSpinner,
                    iconColor: '#3b82f6',
                    iconBg: '#3b82f6',
                  },
                  {
                    label: 'In Transit',
                    value: summary?.in_transit ?? 0,
                    icon: FaTruck,
                    iconColor: '#3b82f6',
                    iconBg: '#3b82f6',
                  },
                  {
                    label: 'Delivered',
                    value: summary?.delivered ?? 0,
                    icon: FaCheckCircle,
                    iconColor: '#10b981',
                    iconBg: '#10b981',
                  },
                  {
                    label: 'Revenue',
                    value: `AED ${summary?.total_payment_collected ?? 0}`,
                    icon: FaDollarSign,
                    iconColor: '#ef4444',
                    iconBg: '#ef4444',
                  },
                  {
                    label: 'Active Drivers',
                    value: summary?.drivers_online ?? 0,
                    icon: FaUser,
                    iconColor: '#3b82f6',
                    iconBg: '#3b82f6',
                  },
                ].map((c, i) => {
                  const IconComponent = c.icon;
                  return (
                    <Col key={i}>
                      <div className='dashboard-card h-100 d-flex flex-column'>
                        <div className='dashboard-card-header d-flex align-items-center justify-content-between mb-3'>
                          <div className='d-flex align-items-center'>
                            <div
                              className='dashboard-card-icon'
                              style={{ backgroundColor: c.iconBg }}
                            >
                              <IconComponent />
                            </div>
                            <span className='dashboard-card-label ms-2'>
                              {c.label}
                            </span>
                          </div>
                          {/* <FaChevronDown className='dashboard-card-chevron' /> */}
                        </div>
                        <h5 className='dashboard-card-value'>{c.value}</h5>
                      </div>
                    </Col>
                  );
                })}
              </Row>

              <Row>
                {/* Branch and Driver List (Sidebar) */}
                <Col md={4}>
                  <Card className='mb-3'>
                    {/* <Card.Header> */}
                    <div
                      style={{
                        background:
                          'linear-gradient(to bottom, #298df7, #134882)',
                        borderRadius: '7px 7px 0 0',
                        color: 'white',
                      }}
                      className='p-2'
                    >
                      <h6 className='mt-2'>Branches & Drivers</h6>
                    </div>
                    {/* </Card.Header> */}
                    <Card.Body className='p-0'>
                      <div style={{ maxHeight: '45vh', overflowY: 'auto' }}>
                        {groupedShowrooms.length === 0 ? (
                          <p className='text-muted text-center py-3 mb-0'>
                            {drivers.length
                              ? 'No showroom mapping found.'
                              : 'No drivers available.'}
                          </p>
                        ) : (
                          groupedShowrooms
                            .filter((group) => {
                              // Filter showrooms: only show if at least one driver has active orders (>0)
                              // Use the same status filter as the per-driver count below so we don't
                              // show drivers who only have historical/completed orders.
                              const driversWithOrders = group.drivers.filter(
                                (driver) => {
                                  const orderCount = orders.filter(
                                    (o) =>
                                      o.driver_id === driver.id &&
                                      [
                                        'assigned',
                                        'accepted',
                                        'in_transit',
                                        'picked_up',
                                      ].includes(o.status)
                                  ).length;
                                  return orderCount > 0;
                                }
                              );
                              return driversWithOrders.length > 0;
                            })
                            .map((group) => {
                              const isSel = selectedShowroom?.id === group.id;
                              // Filter drivers to only show those with active orders (>0)
                              const driversWithOrders = group.drivers.filter(
                                (driver) => {
                                  const orderCount = orders.filter(
                                    (o) =>
                                      o.driver_id === driver.id &&
                                      [
                                        'assigned',
                                        'accepted',
                                        'in_transit',
                                        'picked_up',
                                      ].includes(o.status)
                                  ).length;
                                  return orderCount > 0;
                                }
                              );
                              return (
                                <div key={group.id}>
                                  <div
                                    className={`showroom-header ${
                                      isSel ? 'selected' : ''
                                    }`}
                                    onClick={() => selectShowroom(group)}
                                  >
                                    <div className='d-flex justify-content-between align-items-center'>
                                      <strong>{group.name}</strong>
                                      <small className='text-muted'>
                                        ({driversWithOrders.length} drivers)
                                      </small>
                                    </div>
                                  </div>
                                  {isSel && (
                                    <div className='p-2 bg-light'>
                                      {driversWithOrders.length ? (
                                        driversWithOrders
                                          .filter((driver) => driver.name)
                                          .map((driver) => {
                                            const cnt = orders.filter(
                                              (o) =>
                                                o.driver_id === driver.id &&
                                                [
                                                  'assigned',
                                                  'accepted',
                                                  'in_transit',
                                                  'picked_up',
                                                ].includes(o.status)
                                            ).length;
                                            const isDrvSel =
                                              selectedDriver?.id === driver.id;
                                            return (
                                              <div
                                                key={driver.id}
                                                className={`driver-list-item ${
                                                  isDrvSel ? 'selected' : ''
                                                }`}
                                                onClick={() =>
                                                  selectDriver(driver)
                                                }
                                              >
                                                <div className='driver-name'>
                                                  <div className='driver-avatar'>
                                                    {driver.name?.[0]?.toUpperCase() ??
                                                      '?'}
                                                  </div>
                                                  <span>{driver.name}</span>
                                                </div>
                                                <span className='count-badge'>
                                                  {cnt}
                                                </span>
                                              </div>
                                            );
                                          })
                                      ) : (
                                        <p className='text-muted text-center py-2 mb-0'>
                                          No drivers with orders in this branch.
                                        </p>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            })
                        )}
                      </div>
                    </Card.Body>
                  </Card>

                  {/* Branches & Collections Card */}
                  <Card className='mb-3'>
                    <div
                      style={{
                        background:
                          'linear-gradient(to bottom, #28a745, #1e7e34)',
                        borderRadius: '7px 7px 0 0',
                        color: 'white',
                      }}
                      className='p-2'
                    >
                      <h6 className='mt-2'>Branches & Collections</h6>
                    </div>
                    <Card.Body className='p-0'>
                      <div style={{ maxHeight: '45vh', overflowY: 'auto' }}>
                        {groupedShowroomsCollections.length === 0 ? (
                          <p className='text-muted text-center py-3 mb-0'>
                            {collections.length
                              ? 'No branch mapping found.'
                              : 'No collections available.'}
                          </p>
                        ) : (
                          groupedShowroomsCollections.map((group) => {
                            const isSel =
                              selectedShowroomForCollections?.id === group.id;
                            return (
                              <div key={group.id}>
                                <div
                                  className={`showroom-header ${
                                    isSel ? 'selected' : ''
                                  }`}
                                  onClick={() =>
                                    selectShowroomForCollections(group)
                                  }
                                  style={{ cursor: 'pointer' }}
                                >
                                  <div className='d-flex justify-content-between align-items-center'>
                                    <strong>{group.name}</strong>
                                    <small className='text-muted'>
                                      ({group.collections?.length || 0}{' '}
                                      collections)
                                    </small>
                                  </div>
                                </div>
                                {isSel && (
                                  <div className='p-2 bg-light'>
                                    {group.collections &&
                                    group.collections.length > 0 ? (
                                      <div
                                        style={
                                          {
                                            // maxHeight: '30vh',
                                            // overflowY: 'auto',
                                          }
                                        }
                                      >
                                        {group.collections.map((collection) => {
                                          const isColSel =
                                            selectedCollection?.id ===
                                            collection.id;
                                          return (
                                            <div
                                              key={collection.id}
                                              className={`collection-list-item ${
                                                isColSel ? 'selected' : ''
                                              }`}
                                              onClick={() =>
                                                selectCollection(collection)
                                              }
                                              style={{
                                                cursor: 'pointer',
                                                padding: '8px',
                                                marginBottom: '4px',
                                                borderRadius: '4px',
                                                backgroundColor: isColSel
                                                  ? '#e3f2fd'
                                                  : '#fff',
                                                border: '1px solid #dee2e6',
                                                transition:
                                                  'background-color 0.2s',
                                              }}
                                            >
                                              <div className='d-flex justify-content-between align-items-center'>
                                                <div style={{ flex: 1 }}>
                                                  <strong>
                                                    Collection #{collection.id}
                                                  </strong>
                                                  <div className='text-muted small mt-1'>
                                                    {collection.customer_name ||
                                                      collection.customer
                                                        ?.name ||
                                                      'N/A'}
                                                  </div>
                                                  <div className='text-muted small'>
                                                    AED{' '}
                                                    {collection.amount ||
                                                      '0.00'}{' '}
                                                    •{' '}
                                                    {collection.method || 'N/A'}
                                                  </div>
                                                  <div className='mt-1'>
                                                    <Badge
                                                      bg={
                                                        collection.status ===
                                                          'collected' ||
                                                        collection.status ===
                                                          'completed'
                                                          ? 'success'
                                                          : collection.status ===
                                                            'created'
                                                          ? 'primary'
                                                          : collection.status ===
                                                              'pending' ||
                                                            collection.status ===
                                                              'requested'
                                                          ? 'warning'
                                                          : 'secondary'
                                                      }
                                                      style={{
                                                        fontSize: '0.75rem',
                                                      }}
                                                    >
                                                      {collection.status ||
                                                        'N/A'}
                                                    </Badge>
                                                  </div>
                                                </div>
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    ) : (
                                      <p className='text-muted text-center py-2 mb-0'>
                                        No collections in this branch.
                                      </p>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>

                {/* Assigned Orders Table */}
                <Col md={8}>
                  <Card>
                    <Card.Header className='d-flex justify-content-between align-items-center'>
                      <h6 className='mb-0'>
                        {selectedDriver
                          ? `${selectedDriver.name} – Assigned Orders`
                          : selectedShowroom
                          ? `${selectedShowroom.name} – Select a Driver`
                          : 'Select a Branch & Driver'}
                      </h6>
                      <Button
                        className='new-order-btn'
                        onClick={() => setShowNewOrder(true)}
                      >
                        + New Order
                      </Button>
                    </Card.Header>
                    <Card.Body>
                      {selectedDriver ? (
                        filteredOrders.length ? (
                          <div className='table-responsive mt-3'>
                            <Table bordered striped hover size='sm'>
                              <thead>
                                <tr>
                                  <th>ID / PO</th>
                                  <th>Type</th>
                                  <th>Status</th>
                                  <th>Address</th>{' '}
                                  {/* Consolidated to one column */}
                                  <th>Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {filteredOrders.map((order) => (
                                  <React.Fragment key={order.id}>
                                    <tr>
                                      <td>{order.po_number || order.id}</td>
                                      <td>
                                        {order.type?.replace('_', ' ') || '—'}
                                      </td>
                                      <td>
                                        <span className='status-badge'>
                                          {order.status
                                            ?.replace('_', ' ')
                                            .charAt(0)
                                            .toUpperCase() +
                                            order.status
                                              ?.replace('_', ' ')
                                              .slice(1)
                                              .toLowerCase() || '—'}
                                        </span>
                                      </td>

                                      {/* --- UPDATED TABLE CELL LOGIC --- */}
                                      <td>
                                        <div className='text-muted small mb-1'>
                                          <FaMapMarkerAlt className='text-primary me-1' />
                                          <strong>From:</strong>{' '}
                                          {getPickupAddress(order)}
                                        </div>
                                        <div className='text-muted small'>
                                          <FaTruck className='text-success me-1' />
                                          <strong>To:</strong>{' '}
                                          {getDropAddress(order)}
                                        </div>
                                      </td>
                                      {/* ---------------------------------- */}

                                      <td>
                                        <Button
                                          variant='outline-secondary'
                                          size='sm'
                                          className='me-1'
                                          // Ensure handleShowOrderModal receives the fully mapped 'order' object
                                          onClick={() =>
                                            handleShowOrderModal(order)
                                          }
                                        >
                                          Details
                                        </Button>
                                        <Button
                                          variant='primary'
                                          size='sm'
                                          onClick={() =>
                                            handleTrackDriver(order)
                                          }
                                        >
                                          Track
                                        </Button>
                                      </td>
                                    </tr>
                                  </React.Fragment>
                                ))}
                              </tbody>
                            </Table>
                          </div>
                        ) : (
                          <p className='text-muted text-center py-4'>
                            No assigned orders for this driver.
                          </p>
                        )
                      ) : selectedShowroom ? (
                        <p className='text-muted text-center py-4'>
                          Click on a driver to view their assigned orders.
                        </p>
                      ) : (
                        <p className='text-muted text-center py-4'>
                          Select a branch to view its drivers.
                        </p>
                      )}

                      {/* Assigned Collections Table */}
                      {selectedDriver && filteredCollections.length > 0 && (
                        <div className='mt-4'>
                          <h6 className='mb-3'>
                            {selectedDriver.name} – Assigned Collections
                          </h6>
                          <div className='table-responsive'>
                            <Table bordered striped hover size='sm'>
                              <thead>
                                <tr>
                                  <th>Collection ID</th>
                                  <th>Customer</th>
                                  <th>Amount</th>
                                  <th>Method</th>
                                  <th>Status</th>
                                </tr>
                              </thead>
                              <tbody>
                                {filteredCollections.map((collection) => (
                                  <tr key={collection.id}>
                                    <td>
                                      {collection.collection_code ||
                                        collection.id}
                                    </td>
                                    <td>
                                      {collection.customer?.name || 'N/A'}
                                    </td>
                                    <td>AED {collection.amount}</td>
                                    <td>{collection.method}</td>
                                    <td>
                                      <Badge
                                        bg={getOrderStatusVariant(
                                          collection.status
                                        )}
                                      >
                                        {collection.status?.replace('_', ' ') ||
                                          '—'}
                                      </Badge>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </div>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                  <Card className='mt-3'>
                    <Card.Header className='d-flex justify-content-between align-items-center'>
                      <h6 className='mb-0'>
                        {selectedCollection
                          ? `Collection Details - #${selectedCollection.id}`
                          : selectedShowroomForCollections
                          ? `${selectedShowroomForCollections.name} – Collections`
                          : 'Collection Details'}
                      </h6>
                    </Card.Header>
                    <Card.Body>
                      {selectedCollection ? (
                        <div className='table-responsive mt-3'>
                          <Table bordered striped hover size='sm'>
                            <thead>
                              <tr>
                                <th>Field</th>
                                <th>Value</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td>
                                  <strong>Collection ID</strong>
                                </td>
                                <td>
                                  {selectedCollection.code ||
                                    selectedCollection.collection_code ||
                                    `#${selectedCollection.id}`}
                                </td>
                              </tr>
                              <tr>
                                <td>
                                  <strong>Status</strong>
                                </td>
                                <td>
                                  <Badge
                                    bg={
                                      selectedCollection.status ===
                                        'collected' ||
                                      selectedCollection.status === 'completed'
                                        ? 'success'
                                        : selectedCollection.status ===
                                          'created'
                                        ? 'primary'
                                        : selectedCollection.status ===
                                            'pending' ||
                                          selectedCollection.status ===
                                            'requested'
                                        ? 'warning'
                                        : 'secondary'
                                    }
                                  >
                                    {selectedCollection.status || 'N/A'}
                                  </Badge>
                                </td>
                              </tr>
                              <tr>
                                <td>
                                  <strong>Customer</strong>
                                </td>
                                <td>
                                  {selectedCollection.customer_name ||
                                    selectedCollection.customer?.name ||
                                    'N/A'}
                                </td>
                              </tr>
                              <tr>
                                <td>
                                  <strong>Amount</strong>
                                </td>
                                <td>
                                  AED {selectedCollection.amount || '0.00'}
                                </td>
                              </tr>
                              <tr>
                                <td>
                                  <strong>Method</strong>
                                </td>
                                <td>{selectedCollection.method || 'N/A'}</td>
                              </tr>
                              {selectedCollection.driver_name && (
                                <tr>
                                  <td>
                                    <strong>Driver</strong>
                                  </td>
                                  <td>{selectedCollection.driver_name}</td>
                                </tr>
                              )}
                              {selectedCollection.showroom_name && (
                                <tr>
                                  <td>
                                    <strong>Showroom</strong>
                                  </td>
                                  <td>{selectedCollection.showroom_name}</td>
                                </tr>
                              )}
                              {selectedCollection.created_at && (
                                <tr>
                                  <td>
                                    <strong>Created</strong>
                                  </td>
                                  <td>
                                    {new Date(
                                      selectedCollection.created_at
                                    ).toLocaleString()}
                                  </td>
                                </tr>
                              )}
                              {selectedCollection.assigned_at && (
                                <tr>
                                  <td>
                                    <strong>Assigned</strong>
                                  </td>
                                  <td>
                                    {new Date(
                                      selectedCollection.assigned_at
                                    ).toLocaleString()}
                                  </td>
                                </tr>
                              )}
                              {selectedCollection.collected_at && (
                                <tr>
                                  <td>
                                    <strong>Collected</strong>
                                  </td>
                                  <td>
                                    {new Date(
                                      selectedCollection.collected_at
                                    ).toLocaleString()}
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </Table>
                        </div>
                      ) : selectedShowroomForCollections ? (
                        (() => {
                          const selectedGroup =
                            groupedShowroomsCollections.find(
                              (g) => g.id === selectedShowroomForCollections.id
                            );
                          const branchCollections =
                            selectedGroup?.collections || [];
                          return branchCollections.length > 0 ? (
                            <div className='table-responsive mt-3'>
                              <Table bordered striped hover size='sm'>
                                <thead>
                                  <tr>
                                    <th>Collection ID</th>
                                    <th>Customer</th>
                                    <th>Amount</th>
                                    <th>Method</th>
                                    <th>Status</th>
                                    <th>Driver</th>
                                    <th>Created</th>
                                    <th>Actions</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {branchCollections.map((collection) => (
                                    <tr key={collection.id}>
                                      <td>
                                        {collection.code ||
                                          collection.collection_code ||
                                          `#${collection.id}`}
                                      </td>
                                      <td>
                                        {collection.customer_name ||
                                          collection.customer?.name ||
                                          'N/A'}
                                      </td>
                                      <td>AED {collection.amount || '0.00'}</td>
                                      <td>{collection.method || 'N/A'}</td>
                                      <td>
                                        <Badge
                                          bg={
                                            collection.status === 'collected' ||
                                            collection.status === 'completed'
                                              ? 'success'
                                              : collection.status === 'created'
                                              ? 'primary'
                                              : collection.status ===
                                                  'pending' ||
                                                collection.status ===
                                                  'requested'
                                              ? 'warning'
                                              : 'secondary'
                                          }
                                        >
                                          {collection.status?.replace(
                                            '_',
                                            ' '
                                          ) || 'N/A'}
                                        </Badge>
                                      </td>
                                      <td>
                                        {collection.driver_name ||
                                          collection.driver?.name ||
                                          'Unassigned'}
                                      </td>
                                      <td>
                                        {collection.created_at
                                          ? new Date(
                                              collection.created_at
                                            ).toLocaleDateString()
                                          : 'N/A'}
                                      </td>
                                      <td>
                                        <Button
                                          variant='outline-secondary'
                                          size='sm'
                                          onClick={() =>
                                            selectCollection(collection)
                                          }
                                        >
                                          View Details
                                        </Button>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                            </div>
                          ) : (
                            <p className='text-muted text-center py-4'>
                              No collections in this branch.
                            </p>
                          );
                        })()
                      ) : (
                        <p className='text-muted text-center py-4'>
                          Select a branch to view its collections.
                        </p>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </>
          )}
        </div>

        {/* New Order Modal */}
        <Modal
          size='lg'
          show={showNewOrder}
          onHide={() => {
            setShowNewOrder(false);
            resetNewOrderForm();
          }}
          backdrop='static'
        >
          <Modal.Header closeButton>
            <Modal.Title>New Order</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form onSubmit={handleSubmitOrder}>
              {submitError && <Alert variant='danger'>{submitError}</Alert>}
              <Row className='mb-3'>
                <Col md={6}>
                  <Form.Group controlId='orderType'>
                    <Form.Label>Order Type</Form.Label>
                    <Form.Select
                      value={orderType}
                      onChange={(e) => {
                        setOrderType(e.target.value);
                        // Reset related fields when order type changes
                        setVendorId('');
                        setCustomerId('');
                        setPoNumber('');
                        setInvoiceNumber('');
                        setPendingBalance('');
                        setPoValue('');
                        setRemarks('');
                        setShowroom('');
                        setVendorAddress('');
                        setDropAddress('');
                        setPoFile(null);
                        setNewFromCoords(null);
                        setNewDropCoords(null);
                        setPickupLat('');
                        setPickupLng('');
                        setDeliveryLat('');
                        setDeliveryLng('');
                        setSelectedPoId('');
                      }}
                      required
                    >
                      <option value=''>Select Order Type</option>
                      <option value='purchase_order'>Purchase Order</option>
                      <option value='sales_return'>Sales Return</option>
                      <option value='purchase_return'>Purchase Return</option>
                      {/* <option value="payment_collection">Payment Collection</option> */}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId='assignDriver'>
                    <Form.Label>Assign Driver</Form.Label>

                    <Form.Select
                      value={driverId}
                      onChange={(e) => {setDriverId(e.target.value)
                        setDropAddress('')
                      }}
                      required
                    >
                      <option value=''>Select Driver</option>
                      {allDriversForDropdown.map((d) => (
                        <option key={d.id} value={d.id}>
                          {d.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              {/* Conditional fields based on Order Type */}
              {getNewOrderFields()}

              {/* Showroom field - only show if order type is selected */}
              {orderType && (
                <Form.Group controlId='selectShowroom' className='mb-3'>
                  <Form.Label>Showroom</Form.Label>
                  <Form.Select
                    value={showroom}
                    onChange={(e) => {
                      setShowroom(e.target.value);
                      const selectedShowroom = showrooms.find(
                        (s) => s.id === parseInt(e.target.value)
                      );
                      handleShowroomSelect(selectedShowroom);
                    }}
                  >
                    <option value=''>Select Showroom</option>
                    {filteredShowroomsForDriver.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              )}

              {/* Pickup Address (Source of truth: vendorAddress state) */}
              {orderType && (
                <Form.Group className='mb-3' controlId='pickupAddress'>
                  <Form.Label>Pick-up Location</Form.Label>
                  <Row>
                    <Col md={9}>
                      <Form.Control
                        as='textarea'
                        rows={2}
                        placeholder='Enter pickup address'
                        name='vendorAddress'
                        value={vendorAddress}
                        onChange={(e) => setVendorAddress(e.target.value)}
                        required
                      />
                    </Col>
                  </Row>
                </Form.Group>
              )}

              {/* Drop Location (Source of truth: dropAddress state) */}
              {orderType && (
                <Form.Group className='mb-3' controlId='dropLocation'>
                  <Form.Label>Drop Location</Form.Label>
                  <Row>
                    <Col md={9}>
                      <Form.Control
                        as='textarea'
                        rows={2}
                        placeholder='Enter drop address'
                        name='dropAddress'
                        value={dropAddress}
                        onChange={(e) => setDropAddress(e.target.value)}
                        required
                      />
                    </Col>
                  </Row>
                </Form.Group>
              )}
              {/* Remarks field - only show if order type is selected */}
              {orderType && (
                <Form.Group className='mb-3' controlId='remarks'>
                  <Form.Label>Remarks</Form.Label>
                  <Form.Control
                    as='textarea'
                    rows={2}
                    placeholder='Enter remarks'
                    name='remarks'
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                  />
                </Form.Group>
              )}
              <div className='d-flex justify-content-end'>
                <Button
                  variant='secondary'
                  onClick={() => {
                    setShowNewOrder(false);
                    resetNewOrderForm(); // Ensure modal data is cleared on cancel
                  }}
                  className='me-2'
                >
                  Cancel
                </Button>
                <Button
                  type='submit'
                  variant='primary'
                  disabled={!orderType || submitLoading}
                >
                  {submitLoading ? (
                    <Spinner animation='border' size='sm' className='me-2' />
                  ) : (
                    'Create Order'
                  )}
                </Button>
              </div>
            </Form>
          </Modal.Body>
        </Modal>

        {/* Order Details Modal (Local Definition) - Using the imported component is preferred */}
        <AdminOrderDetailsModal
          show={showOrderModal}
          order={selectedOrder}
          handleClose={handleCloseOrderModal}
        />
        {/* OrderTracking modal component */}
        <OrderTracking
          show={showTrackModal}
          handleClose={handleCloseTrackModal}
          order={trackOrder}
          driverId={trackOrder?.driver_id} // Pass driverId from the order
        />
      </Container>
    </div>
  );
};

export default AdminDashboard;
